import type { FavoriteList, Restaurant } from "@/API";
import { create } from "zustand";
import { generateClient } from "aws-amplify/api";
import {
  getFavoriteLists as getFavoriteListsQuery,
  getRestaurantsInFavoriteList,
} from "@/graphql/queries";
import {
  addRestaurantsToFavoriteList,
  createFavoriteList,
  deleteFavoriteList as deleteFavoriteListMutation,
  removeRestaurantFromFavoriteList as removeRestaurantFromFavoriteListMutation,
} from "@/graphql/mutations";

const client = generateClient();

export type FavoriteListRestaurants = {
  [key: string]: {
    // key is favoriteId
    favorite: FavoriteList;
    restaurants: Restaurant[];
  };
};

type LocalState = {
  data: FavoriteListRestaurants | null;
  isLoading: boolean;
  error: Error | null;
  isFetching: boolean;
  addingRestaurantToFavoriteList: boolean;
  addedRestaurantToFavoriteList: boolean;
  deletingFavoriteList: boolean;
  deletedFavoriteList: boolean;
  restaurantJustAdded: Restaurant | null;
  restaurantsJustAdded: boolean;
  favoriteNameListJustAdded: String | null;
};

export const userFavoriteListState = create<LocalState>(() => ({
  data: null,
  isLoading: false,
  error: null,
  isFetching: false,
  addingRestaurantToFavoriteList: false,
  restaurantsInFavoriteList: [],
  addedRestaurantToFavoriteList: false,
  deletingFavoriteList: false,
  deletedFavoriteList: false,
  restaurantJustAdded: null,
  restaurantsJustAdded: false,
  favoriteNameListJustAdded: null,
}));

export const getUserFavoriteListIsloading = () =>
  userFavoriteListState.setState(() => ({
    isLoading: true,
    isFetching: false,
    data: null,
    error: null,
  }));
export const getUserFavoriteListIsError = (error: Error) =>
  userFavoriteListState.setState(() => ({
    error: error,
    data: null,
    isLoading: false,
    isFetching: false,
  }));
export const getUserFavoriteListIsSuccess = (
  queryResult: FavoriteListRestaurants,
) =>
  userFavoriteListState.setState(() => ({
    data: queryResult,
    isLoading: false,
    error: null,
    isFetching: false,
  }));

export const initAddedRestaurantInFavoriteList = () => {
  userFavoriteListState.setState(() => ({
    restaurantJustAdded: null,
    restaurantsJustAdded: false,
    favoriteNameListJustAdded: null,
  }));
};

export const getUserFavoriteList = async () => {
  getUserFavoriteListIsloading();

  try {
    const { data } = await client.graphql({
      query: getFavoriteListsQuery,
    });

    const getFavoriteLists = data.getFavoriteLists.filter(
      (favoriteList) => favoriteList !== null,
    );

    const newFavoriteListRestaurants: FavoriteListRestaurants = {};

    if (getFavoriteLists) {
      Promise.all(
        getFavoriteLists.map(async (favoriteList) => {
          const data = await client.graphql({
            query: getRestaurantsInFavoriteList,
            variables: {
              favoriteListId: favoriteList.id,
            },
          });
          newFavoriteListRestaurants[favoriteList.id] = {
            favorite: favoriteList,
            restaurants:
              data.data.getRestaurantsInFavoriteList.filter(
                (restaurant) => restaurant !== null,
              ) ?? [],
          };
        }),
      ).then(() => {
        getUserFavoriteListIsSuccess(newFavoriteListRestaurants);
      });
    }
  } catch (e) {
    const errorMessage =
      e instanceof Error ? e.message : "failed to query getFavoriteLists";
    getUserFavoriteListIsError(Error(errorMessage));
  }
};

export const resetCreateFavoriteListAndAddRestaurantToFavoriteList = () => {
  userFavoriteListState.setState(() => ({
    addingRestaurantToFavoriteList: false,
    addedRestaurantToFavoriteList: false,
  }));
};

type PayloadCreateFavoriteListAndAddRestaurantToFavoriteList = {
  favoriteListName: string;
  restaurantsId: string[];
};

export const createFavoriteListAndAddRestaurantToFavoriteList = async (
  payload: PayloadCreateFavoriteListAndAddRestaurantToFavoriteList,
) => {
  try {
    userFavoriteListState.setState(() => ({
      addingRestaurantToFavoriteList: true,
      addedRestaurantToFavoriteList: false,
    }));

    const { data: dataCreateFavoriteList } = await client.graphql({
      query: createFavoriteList,
      variables: {
        name: payload.favoriteListName,
      },
    });

    if (dataCreateFavoriteList.createFavoriteList) {
      const { data: addRestaurant } = await client.graphql({
        query: addRestaurantsToFavoriteList,
        variables: {
          restaurantIds: payload.restaurantsId,
          favoriteListId: dataCreateFavoriteList.createFavoriteList.id,
        },
      });

      const newFavoriteListRestaurants: FavoriteListRestaurants =
        userFavoriteListState.getState().data ?? {};

      let restaurantsToAdd: Restaurant[] = [];

      if (addRestaurant.addRestaurantsToFavoriteList) {
        const { data: restaurantsQuery } = await client.graphql({
          query: getRestaurantsInFavoriteList,
          variables: {
            favoriteListId: dataCreateFavoriteList.createFavoriteList.id,
          },
        });

        restaurantsToAdd = restaurantsQuery.getRestaurantsInFavoriteList.filter(
          (restaurant) => restaurant !== null,
        );

        if (payload.restaurantsId.length === 1) {
          const restaurantAdded =
            restaurantsQuery.getRestaurantsInFavoriteList.find(
              (restaurant) => restaurant?.id === payload.restaurantsId[0],
            );

          if (restaurantAdded) {
            userFavoriteListState.setState(() => ({
              restaurantJustAdded: restaurantAdded,
              favoriteNameListJustAdded: payload.favoriteListName,
              restaurantsJustAdded: false,
            }));
          }
        } else {
          userFavoriteListState.setState(() => ({
            restaurantJustAdded: null,
            favoriteNameListJustAdded: payload.favoriteListName,
            restaurantsJustAdded: true,
          }));
        }
      }

      newFavoriteListRestaurants[dataCreateFavoriteList.createFavoriteList.id] =
        {
          favorite: {
            ...dataCreateFavoriteList.createFavoriteList,
            restaurantCount: payload.restaurantsId.length,
          },
          restaurants: restaurantsToAdd,
        };

      userFavoriteListState.setState(() => ({
        addedRestaurantToFavoriteList: true,
        addingRestaurantToFavoriteList: false,
        data: newFavoriteListRestaurants,
      }));
    }
  } catch {
    /* TODO: handle error */
  }
};

type PayloadAddRestaurantFavoriteList = {
  favoriteListId: string;
  favoriteListName: string;
  restaurantsId: string[];
};

export const addRestaurantFavoriteList = async (
  payload: PayloadAddRestaurantFavoriteList,
) => {
  const { favoriteListId, favoriteListName, restaurantsId } = payload;

  try {
    userFavoriteListState.setState(() => ({
      addingRestaurantToFavoriteList: true,
      addedRestaurantToFavoriteList: false,
    }));
    const { data: addRestaurant } = await client.graphql({
      query: addRestaurantsToFavoriteList,
      variables: {
        favoriteListId,
        restaurantIds: restaurantsId,
      },
    });

    const newFavoriteListRestaurants: FavoriteListRestaurants =
      userFavoriteListState.getState().data ?? {};

    if (addRestaurant.addRestaurantsToFavoriteList) {
      const { data: restaurantsQuery } = await client.graphql({
        query: getRestaurantsInFavoriteList,
        variables: {
          favoriteListId: favoriteListId,
        },
      });

      if (
        newFavoriteListRestaurants &&
        newFavoriteListRestaurants[favoriteListId]
      ) {
        newFavoriteListRestaurants[favoriteListId].restaurants =
          restaurantsQuery.getRestaurantsInFavoriteList.filter(
            (restaurant) => restaurant !== null,
          );

        newFavoriteListRestaurants[favoriteListId].favorite.restaurantCount =
          newFavoriteListRestaurants[favoriteListId].favorite.restaurantCount +
          restaurantsId.length;

        if (restaurantsId.length === 1) {
          const restaurantAdded =
            restaurantsQuery.getRestaurantsInFavoriteList.find(
              (restaurant) => restaurant?.id === restaurantsId[0],
            );

          if (restaurantAdded) {
            userFavoriteListState.setState(() => ({
              restaurantJustAdded: restaurantAdded,
              favoriteNameListJustAdded: favoriteListName,
              restaurantsJustAdded: false,
            }));
          }
        } else {
          userFavoriteListState.setState(() => ({
            restaurantJustAdded: null,
            favoriteNameListJustAdded: favoriteListName,
            restaurantsJustAdded: true,
          }));
        }
      }
    }

    userFavoriteListState.setState(() => ({
      addedRestaurantToFavoriteList: true,
      addingRestaurantToFavoriteList: false,
      data: newFavoriteListRestaurants,
    }));
  } catch {
    /* TODO: handle error */
  }
};

export type PlayloadDeleteFavoriteList = {
  favoriteListId: string;
};

export const deleteFavoriteList = async (
  payload: PlayloadDeleteFavoriteList,
) => {
  try {
    userFavoriteListState.setState(() => ({
      deletedFavoriteList: false,
      deletingFavoriteList: true,
    }));

    const { data } = await client.graphql({
      query: deleteFavoriteListMutation,
      variables: {
        favoriteListId: payload.favoriteListId,
      },
    });

    if (data.deleteFavoriteList) {
      const newFavoriteListRestaurants: FavoriteListRestaurants =
        userFavoriteListState.getState().data ?? {};

      delete newFavoriteListRestaurants[payload.favoriteListId];

      userFavoriteListState.setState(() => ({
        deletedFavoriteList: true,
        deletingFavoriteList: false,
        data: newFavoriteListRestaurants,
      }));
    }
  } catch {
    /* TODO: handle error */
  }
};

export const resetDeleteFavoriteList = () => {
  userFavoriteListState.setState(() => ({
    deletedFavoriteList: false,
    deletingFavoriteList: false,
  }));
};

export const removeRestaurantFromFavoriteList = async ({
  restaurantId,
}: {
  restaurantId: string;
}) => {
  const newFavoriteListRestaurants: FavoriteListRestaurants =
    userFavoriteListState.getState().data ?? {};

  const favoriteIdsToRemoveRestaurant: string[] = [];
  const favoriteIdsToDelete: string[] = [];

  Object.entries(newFavoriteListRestaurants).forEach(([favoriteId, value]) => {
    const { restaurants } = value;
    const indexRestaurantToRemove = restaurants.findIndex(
      (restaurant) => restaurant.id === restaurantId,
    );
    if (indexRestaurantToRemove !== -1) {
      favoriteIdsToRemoveRestaurant.push(favoriteId);
    }
  });

  favoriteIdsToRemoveRestaurant.forEach((favoriteId) => {
    newFavoriteListRestaurants[favoriteId].restaurants =
      newFavoriteListRestaurants[favoriteId].restaurants.filter(
        (restaurant) => restaurant.id !== restaurantId,
      );
  });

  favoriteIdsToRemoveRestaurant.forEach((favoriteId) => {
    if (newFavoriteListRestaurants[favoriteId].restaurants.length === 0) {
      favoriteIdsToDelete.push(favoriteId);
      delete newFavoriteListRestaurants[favoriteId];
    }
  });

  userFavoriteListState.setState(() => ({
    data: newFavoriteListRestaurants,
  }));

  try {
    Promise.all(
      favoriteIdsToRemoveRestaurant.map(async (favoriteId) => {
        await client.graphql({
          query: removeRestaurantFromFavoriteListMutation,
          variables: {
            favoriteListId: favoriteId,
            restaurantId,
          },
        });
      }),
    );
    Promise.all(
      favoriteIdsToDelete.map(async (favoriteId) => {
        await deleteFavoriteList({
          favoriteListId: favoriteId,
        });
      }),
    );
  } catch {}
};
